import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SecurityAlert {
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  count: number;
  timeframe: string;
  recommendation: string;
}

interface SecuritySummary {
  failed_logins_24h: number;
  admin_actions_24h: number;
  password_resets_24h: number;
  scan_timestamp: string;
}

interface SecurityValidation {
  validation_timestamp: string;
  security_level: 'moderate' | 'high' | 'maximum';
  rls_enabled_tables: number;
  total_policies: number;
  security_functions: number;
  rls_tables: string[];
  recommendations: string[];
}

interface AdvancedSecurityData {
  alerts: SecurityAlert[];
  summary: SecuritySummary;
  validation: SecurityValidation;
}

export const useAdvancedSecurityMonitoring = () => {
  const [securityData, setSecurityData] = useState<AdvancedSecurityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runComprehensiveSecurityScan = async () => {
    setLoading(true);
    setError(null);

    try {
      // Run security anomaly detection
      const { data: anomalyData, error: anomalyError } = await supabase.rpc('detect_security_anomalies');
      
      if (anomalyError) {
        throw new Error(`Anomaly detection failed: ${anomalyError.message}`);
      }

      // Get security monitoring summary using secure function
      const { data: summaryData, error: summaryError } = await supabase.rpc('get_security_monitoring_summary');

      if (summaryError) {
        throw new Error(`Summary fetch failed: ${summaryError.message}`);
      }

      // Run security configuration validation
      const { data: validationData, error: validationError } = await supabase.rpc('validate_security_configuration');
      
      if (validationError) {
        throw new Error(`Validation failed: ${validationError.message}`);
      }

      const combinedData: AdvancedSecurityData = {
        alerts: (anomalyData as any)?.alerts || [],
        summary: Array.isArray(summaryData) ? summaryData[0] : summaryData,
        validation: validationData as unknown as SecurityValidation
      };

      setSecurityData(combinedData);
    } catch (err) {
      console.error('Security monitoring error:', err);
      setError(err instanceof Error ? err.message : 'Failed to run security scan');
    } finally {
      setLoading(false);
    }
  };

  const logSecurityEvent = async (
    eventType: string, 
    eventData?: Record<string, any>, 
    userContext?: Record<string, any>,
    severity: 'low' | 'medium' | 'high' | 'critical' = 'medium'
  ) => {
    try {
      const { error } = await supabase.rpc('log_security_event_enhanced', {
        event_type: eventType,
        event_data: eventData || {},
        user_context: userContext || {},
        severity: severity
      });

      if (error) {
        console.error('Failed to log security event:', error);
      }
    } catch (err) {
      console.error('Security event logging error:', err);
    }
  };

  // Auto-refresh security data every 5 minutes
  useEffect(() => {
    runComprehensiveSecurityScan();
    
    const interval = setInterval(() => {
      runComprehensiveSecurityScan();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return {
    securityData,
    loading,
    error,
    runComprehensiveSecurityScan,
    logSecurityEvent
  };
};